
import React, { useState, useEffect, useRef } from "react";
import { Modal, Form, Input, Select, Button } from "antd";
import { Toast as PrimeToast } from "primereact/toast";
import SmartVitalUploadProgress from "./SmartVitalUploadProgress";
import axios from "axios";
import http from '../../api/Services/HttpCommon';// 
import { useNavigate } from "react-router-dom";

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const PATIENT_INFO_BASE = process.env.REACT_APP_QUANTUMURL;

export default function SmartVitalUpload({
  patientId,
  serviceName = "Smart Vitals",
  onStarted,
  initialVisible,
  onCloseInitial,
  autoUploadFile,
  onUploadComplete,
  onStageChange,
}) {
  const [file, setFile]             = useState(null);
  const [requestId, setRequestId]   = useState(null);
  const [uploadUrls, setUploadUrls] = useState([]);
  const [stage, setStage]           = useState(initialVisible ? "instructions" : null);
  const [progress, setProgress]     = useState(0);
  const [errorText, setErrorText]   = useState("");

  // ── Profile-incomplete state (mirrors LabTestCart exactly) ───────────────
  const [profileIncomplete, setProfileIncomplete]             = useState(false);
  const [profileIncompleteFields, setProfileIncompleteFields] = useState({
    gender: false,
    name:   false,
    age:    false,
    mobile: false,
  });
  const [genderValue, setGenderValue] = useState("");
  const [nameValue,   setNameValue]   = useState("");
  const [ageValue,    setAgeValue]    = useState("");
  const [mobileValue, setMobileValue] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);

  // pending file so upload resumes after profile is saved
  const [pendingFile, setPendingFile] = useState(null);

  const primeToast   = useRef(null);
  const navigate     = useNavigate();
  const endpointBase = process.env.REACT_APP_QUANTUMURL;

  const log = (event, payload) => {
    try { console.log("[SmartVitalUpload]", event, payload || ""); } catch {}
  };

  // Lets the parent hide the scan screen once the upload page takes over.
  useEffect(() => {
    if (typeof onStageChange === "function") onStageChange(stage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // ── Reset on external close ───────────────────────────────────────────────
  useEffect(() => {
    if (!initialVisible) {
      setStage(null);
      setProgress(0);
      setErrorText("");
    }
  }, [initialVisible, autoUploadFile]);

  // ── 1. GET /patient-info/{patientId} → find missing fields ───────────────
  const checkPatientProfile = async (fileToUpload) => {
    try {
      const res     = await axios.get(
        `${PATIENT_INFO_BASE}patient-info/${patientId}`,
        { headers: { accept: "application/json" } }
      );
      const patient = res?.data?.data?.[0] || {};
      log("patient_info_fetched", patient);

      const missing = {
        name:   !patient.PATIENTNAME || patient.PATIENTNAME.toString().trim() === "",
        age:    !patient.AGE         || patient.AGE.toString().trim()         === "",
        gender: !patient.GENDER      || patient.GENDER.toString().trim()      === "",
        mobile: !patient.MOBILE      || patient.MOBILE.toString().trim()      === "",
      };

      const hasMissing = Object.values(missing).some(Boolean);

      if (!hasMissing) {
        // All good — proceed directly to upload
        return true;
      }

      // Pre-fill whatever IS already present so user only types what's missing
      if (patient.PATIENTNAME) setNameValue(patient.PATIENTNAME);
      if (patient.AGE)         setAgeValue(String(patient.AGE));
      if (patient.GENDER)      setGenderValue(patient.GENDER);
      if (patient.MOBILE)      setMobileValue(patient.MOBILE);

      setPendingFile(fileToUpload);
      setProfileIncompleteFields(missing);
      setProfileIncomplete(true);
      log("profile_incomplete", missing);
      return false; // upload paused — waiting for modal
    } catch (err) {
      log("patient_info_error", err);
      // If check itself fails, don't block the upload
      return true;
    }
  };

  // ── 2. patient/update/{code} → same API + http.put as LabTestCart ────────
  const handleProfileOk = async () => {
    // Validate only the fields that are actually missing
    if (profileIncompleteFields.name && !nameValue.trim()) {
      primeToast.current?.show({ severity: "warn", summary: "Please enter your full name", life: 3000 });
      return;
    }
    if (profileIncompleteFields.age && (!ageValue || isNaN(ageValue) || Number(ageValue) < 1)) {
      primeToast.current?.show({ severity: "warn", summary: "Please enter a valid age", life: 3000 });
      return;
    }
    if (profileIncompleteFields.gender && !genderValue) {
      primeToast.current?.show({ severity: "warn", summary: "Please select a gender", life: 3000 });
      return;
    }
    if (profileIncompleteFields.mobile && !/^[6-9]\d{9}$/.test(mobileValue)) {
      primeToast.current?.show({ severity: "warn", summary: "Please enter a valid 10-digit mobile number", life: 3000 });
      return;
    }

    setProfileSaving(true);
    try {
      // Build payload with only the fields that were missing — exactly like LabTestCart handleOk
      const payload = {};
      if (profileIncompleteFields.name)   payload.name   = nameValue.trim();
      if (profileIncompleteFields.age)    payload.age    = ageValue;
      if (profileIncompleteFields.gender) payload.gender = genderValue;
      if (profileIncompleteFields.mobile) payload.mobile = mobileValue;

      
        const url = `${process.env.REACT_APP_BASEURL}patient/update/${patientId}`;
        const response = await http.put(url, payload);
        console.log("Profile update response:", response.data);
      primeToast.current?.show({ severity: "success", summary: "Profile updated successfully", life: 3000 });

      // Reset modal state
      setProfileIncomplete(false);
      setProfileIncompleteFields({ gender: false, name: false, age: false, mobile: false });
      setGenderValue(""); setNameValue(""); setAgeValue(""); setMobileValue("");

      // Resume the upload that was waiting
      const fileToUse = pendingFile || autoUploadFile;
      setPendingFile(null);
      if (fileToUse) await runUploadFlow(fileToUse);
    } catch (err) {
      log("profile_update_error", err);
      primeToast.current?.show({ severity: "error", summary: "Failed to update profile. Please try again.", life: 3000 });
    } finally {
      setProfileSaving(false);
    }
  };

  const handleProfileCancel = () => {
    setProfileIncomplete(false);
    setProfileIncompleteFields({ gender: false, name: false, age: false, mobile: false });
    setGenderValue(""); setNameValue(""); setAgeValue(""); setMobileValue("");
    setPendingFile(null);
    if (typeof onCloseInitial === "function") onCloseInitial();
  };

  // ── 3. Core upload flow ───────────────────────────────────────────────────
  const runUploadFlow = async (fileToUse) => {
    setFile(fileToUse);
    setStage("ai");
    setErrorText("");

    try {
      const { rid, urls } = await generateUploadUrl("video/mp4", fileToUse);
      await uploadFile(urls, fileToUse);
      if (typeof onUploadComplete === "function") await onUploadComplete(rid);
      await checkUploaded(rid);
    } catch (err) {
      setProgress(0);
      setStage("ai");
      setErrorText(String(err?.message || err));
      try { localStorage.removeItem("quantumUploadSession"); } catch {}
    }
  };

  // ── autoUploadFile trigger — profile check runs first ────────────────────
  useEffect(() => {
    console.log("[SmartVitalUpload] autoUploadFile changed:", autoUploadFile);
    if (!autoUploadFile) return;

    let cancelled = false;
    (async () => {
      const profileOk = await checkPatientProfile(autoUploadFile);
      if (cancelled || !profileOk) return; // modal shown — wait for user
      await runUploadFlow(autoUploadFile);
    })();

    return () => { cancelled = true; };
  }, [autoUploadFile]);

  // ── Upload helpers (unchanged from original) ──────────────────────────────
  const generateUploadUrl = async (ct, selectedFile) => {
    setProgress(10);
    try {
      localStorage.setItem(
        "quantumUploadSession",
        JSON.stringify({ requestId: null, serviceName, progress: 10, checkedOnce: false })
      );
    } catch {}
    setStage("ai");

    const payload = {
      patientId,
      serviceName: "Smart Vitals",
      files: [{ fileNumber: 1, fileType: "mp4", contentType: "video/mp4" }],
    };

    try {
      await delay(5000);
      const url = new URL("new_generate-resumable-url", endpointBase).toString();
      const res  = await axios.post(url, payload, { headers: { "Content-Type": "application/json" } });
      const data = res.data;
      const rid  = data?.request_id || data?.requestId || data?.requestid || data?.requestID || null;

      setRequestId(rid);
      setUploadUrls(data?.upload_urls || []);
      setProgress(50);
      try {
        const sess = JSON.parse(localStorage.getItem("quantumUploadSession") || "{}");
        localStorage.setItem("quantumUploadSession", JSON.stringify({ ...sess, requestId: rid, serviceName, progress: 50 }));
      } catch {}
      setStage("progress");
      setErrorText("");
      return { rid, urls: data?.upload_urls || [] };
    } catch (err) {
      setErrorText(String(err));
      setStage("ai");
      localStorage.removeItem("quantumUploadSession");
      return Promise.reject(err);
    }
  };

  const uploadFile = async (urlsParam, fileParam) => {
    const urls    = Array.isArray(urlsParam) ? urlsParam : uploadUrls;
    const useFile = fileParam || file;
    if (!urls.length || !useFile) return;
    setStage("progress");

    try {
      const cleanUrl = urls[0]?.url?.trim();
      if (!cleanUrl) throw new Error("Missing upload URL");

      await delay(1000);
      await axios.put(cleanUrl, useFile, {
        headers: { "Content-Type": "video/mp4" },
        timeout: 300000,
        onUploadProgress: (evt) => {
          if (evt?.total > 0) {
            const pct = 50 + Math.min(30, Math.round((evt.loaded / evt.total) * 30));
            setProgress(pct);
            try {
              const sess = JSON.parse(localStorage.getItem("quantumUploadSession") || "{}");
              localStorage.setItem("quantumUploadSession", JSON.stringify({ ...sess, progress: pct }));
            } catch {}
          }
        },
      });
      setProgress(80);
      try {
        const sess = JSON.parse(localStorage.getItem("quantumUploadSession") || "{}");
        localStorage.setItem("quantumUploadSession", JSON.stringify({ ...sess, progress: 80 }));
      } catch {}
      setErrorText("");
    } catch (err) {
      setErrorText(String(err));
      throw err;
    }
  };

  const checkUploaded = async (ridOverride) => {
    const rid = ridOverride || requestId;
    if (!rid) return;
    setProgress((p) => (p < 90 ? 90 : p));
    try {
      const sess = JSON.parse(localStorage.getItem("quantumUploadSession") || "{}");
      localStorage.setItem("quantumUploadSession", JSON.stringify({ ...sess, progress: 90 }));
    } catch {}

    const url  = new URL("check-uploaded-files", endpointBase).toString();
    const body = { requestId: rid, serviceName: "Smart Vitals", signedUrlCount: 1, fileType: "mp4" };

    try {
      await delay(2000);
      const res = await axios.post(url, body, {
        headers: { "Content-Type": "application/json", accept: "application/json" },
      });
      if (res.data) {
        setProgress(100);
        try {
          const sess = JSON.parse(localStorage.getItem("quantumUploadSession") || "{}");
          localStorage.setItem("quantumUploadSession", JSON.stringify({ ...sess, progress: 100 }));
        } catch {}
        setErrorText("");
      } else {
        setErrorText("Upload verification failed.");
      }
    } catch (err) {
      setErrorText(String(err));
      throw err;
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <PrimeToast ref={primeToast} />

      {/* ── Profile Incomplete Modal — */}
      <Modal
        open={profileIncomplete}
  title="Uhmm!! Seems like your profile is Incomplete"
  maskStyle={{ backdropFilter: "blur(4px)", backgroundColor: "rgba(0,0,0,0.45)" }}
        onOk={handleProfileOk}
        onCancel={handleProfileCancel}
        footer={[
          <Button key="back" onClick={handleProfileCancel}>
            Return
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={profileSaving}
            onClick={handleProfileOk}
          >
            Submit
          </Button>,
        ]}
      >
        <Form layout="vertical">
          {profileIncompleteFields.name && (
            <Form.Item label="Full Name" required>
              <Input
                placeholder="Enter your full name"
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
              />
            </Form.Item>
          )}

          {profileIncompleteFields.age && (
            <Form.Item label="Age" required>
              <Input
                type="number"
                placeholder="Enter your age"
                value={ageValue}
                onChange={(e) => setAgeValue(e.target.value)}
              />
            </Form.Item>
          )}

          {profileIncompleteFields.gender && (
            <Form.Item label="Gender" required>
              <Select
                placeholder="Select your gender"
                value={genderValue || undefined}
                onChange={(val) => setGenderValue(val)}
              >
                <Select.Option value="Male">Male</Select.Option>
                <Select.Option value="Female">Female</Select.Option>
                <Select.Option value="O">Other</Select.Option>
              </Select>
            </Form.Item>
          )}

          {profileIncompleteFields.mobile && (
            <Form.Item label="Mobile Number" required>
              <Input
                type="tel"
                maxLength={10}
                placeholder="Enter 10-digit mobile number"
                value={mobileValue}
                onChange={(e) => setMobileValue(e.target.value)}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* ── Upload complete / error screen ── */}
      {/* Rendered inline under the "Your Face Scan is Complete!" camera state in VideoCapture */}
      {stage === "ai" && (
        <div className="sv-upload-box">
          {errorText && <h3>Upload failed!</h3>}
          <p className="sv-upload-msg">
            {errorText
              ? "Your video upload was unsuccessful - please upload again!"
              : "Your video will be securely uploaded and prepared for AI analysis. Once processed, our system will read your heart rate, oxygen level, breathing, blood pressure and stress markers to generate your personalized vitals report. You can explore other services while this step is completed."}
          </p>

            <div className={`sv-upload-actions${errorText ? " is-single" : ""}`}>
              {errorText ? (
                <button
                  onClick={() => {
                    setStage(null);
                    if (typeof onCloseInitial === "function") onCloseInitial();
                    setErrorText("");
                    try {
                      const sess = JSON.parse(localStorage.getItem("quantumUploadSession") || "{}");
                      localStorage.setItem("quantumUploadSession", JSON.stringify({ ...sess, checkedOnce: true }));
                    } catch {}
                  }}
                  className="sv-btn sv-btn-primary"
                >
                  Try Again
                </button>
              ) : (
                <>
                  <button
                    onClick={() => setStage("progress")}
                    className="sv-btn sv-btn-ghost"
                  >
                    Wait Here
                  </button>
                  <button
                    onClick={() => {
                      try {
                        const sess = JSON.parse(localStorage.getItem("quantumUploadSession") || "{}");
                        localStorage.setItem("quantumUploadSession", JSON.stringify({ ...sess, checkedOnce: true }));
                      } catch {}
                      navigate("/");
                    }}
                    className="sv-btn sv-btn-primary"
                  >
                    Explore Services
                  </button>
                </>
              )}
            </div>
        </div>
      )}

      <SmartVitalUploadProgress
        solutionType="smart_care"
        visible={stage === "progress"}
        progress={progress}
        errorText={errorText}
        onView={() => {
          navigate("/aireports", { state: { tab: "smart_vitals" } });
          log("progress_modal_view_report", { tab: "smart_vitals" });
          localStorage.removeItem("quantumUploadSession");
        }}
      />
    </>
  );
}